// The ONE place evaluation selection lives (F4.6 Amendment 2).
//
// The ruling's requirement is that the results screen, the report and the
// comparison cannot drift apart on which evaluation represents a run. So the
// ordering rule (by `seq`, the monotonic identity column — NEVER
// `evaluated_at`, the transaction timestamp that ties for rows written
// together) and the comparison-eligibility predicate both live here, and
// every surface imports them:
//
//   - results + report render the LATEST evaluation, eligible or not — an
//     invalidated latest evaluation is an honest display state there;
//   - the comparison takes each side's latest COMPARISON-ELIGIBLE
//     evaluation: structurally valid frozen snapshot, validated, and not
//     invalidated by the qualification rules.

import type { EvaluationRow } from "./types";

/** The recorded persistence shape is the EvaluationSnapshot ENVELOPE whose
 *  `result` member carries the kernel's EvaluationResult (the 0014 lesson:
 *  interpret the RECORDED shape, exactly, no fallback). "Structurally valid"
 *  here means the members every consumer reads are present with their
 *  contract types — anything less fails closed. */
export function snapshotStructurallyValid(result: unknown): boolean {
  if (typeof result !== "object" || result === null) return false;
  const inner = (result as Record<string, unknown>).result;
  if (typeof inner !== "object" || inner === null) return false;
  const r = inner as Record<string, unknown>;
  return (
    typeof r.overall === "object" && r.overall !== null &&
    Array.isArray(r.blocks) &&
    Array.isArray(r.dimensions) &&
    Array.isArray(r.findings)
  );
}

/** Comparison eligibility (Amendment 2): a structurally valid frozen
 *  snapshot, not rejected by validation, not invalidated by qualification. */
export function isComparisonEligible(row: EvaluationRow): boolean {
  return (
    row.validation_ok &&
    row.qualification_status !== "invalidated" &&
    snapshotStructurallyValid(row.result)
  );
}

/** Latest by `seq` — the results/report selection. */
export function selectLatestEvaluation(rows: EvaluationRow[]): EvaluationRow | null {
  let best: EvaluationRow | null = null;
  for (const row of rows) if (best === null || row.seq > best.seq) best = row;
  return best;
}

/** Latest comparison-eligible by `seq` — the comparison selection. */
export function selectEligibleEvaluation(rows: EvaluationRow[]): EvaluationRow | null {
  return selectLatestEvaluation(rows.filter(isComparisonEligible));
}
